/**
 * Tab registry — maps a stable `TabKey` to its `{ icon, label }`, and groups keys
 * by persona via `TABS_BY_TYPE`.
 *
 * `<BottomTab>` takes an array of these keys (e.g. `["home", "parentReports"]`) and
 * renders each one; the icon + label are looked up here so callers never repeat glyph
 * or label strings. Keys are CamelCase DS vocabulary, decoupled from navigation route
 * names — a screen maps its own routes onto these keys.
 *
 * `<PageTemplate>` picks a set via `tabType` when `showBottomTab` is on (Storybook).
 * Production tabs live on the navigator `tabBar`. Navigation stays outside the DS.
 *
 * Labels mirror the app's tab `headerTitle`s; icons are FontAwesome5 glyphs (typed via
 * the `<Icon>` atom's `IconName`). This is presentational config only — no domain imports.
 */
import { type IconName } from "../../atoms/icons/Icon"

export type TabItem = {
    icon: IconName
    label: string
}

export const TAB_ITEMS = {
    // Student — le glyphe des activités est celui du parent : le même objet, le même dessin.
    home: { icon: "home", label: "Accueil" },
    activities: { icon: "book-open", label: "Activités" },
    // « Messages » (Wajih, 19/10 : l'ancien « Mon tutorat ») : ses tuteurs et leurs conversations.
    tutor: { icon: "comment-dots", label: "Messages" },
    // Tutor — les libellés des onglets d'aujourd'hui (`tutorTabScreens.js`, leurs `headerTitle`),
    // sauf le premier : « Accueil » (Wajih, 28/10), l'ancien « Tableau de bord ».
    dashboard: { icon: "home", label: "Accueil" },
    schools: { icon: "school", label: "Écoles" },
    classrooms: { icon: "users", label: "Mes classes" },
    students: { icon: "user-graduate", label: "Mes élèves" },
    proposals: { icon: "hand-holding-usd", label: "Offres de cours" },
    activitiesReview: { icon: "tasks", label: "Revue" },
    // « Ressources » (la maquette A1) : « Ressources pédagogiques », l'ancien `headerTitle`, passait
    // à la ligne sur un téléphone et cassait la barre en deux rangées.
    tutorResources: { icon: "book", label: "Ressources" },
    adminActions: { icon: "cog", label: "Actions" },
    referralsBackoffice: { icon: "share-alt", label: "Affiliations" },
    // Parent — les quatre glyphes de la maquette validée : tableau de bord, livre ouvert,
    // document, carte bancaire.
    parentHome: { icon: "layout-dashboard", label: "Accueil" },
    parentActivities: { icon: "book-open", label: "Activités" },
    parentReports: { icon: "file-lines", label: "Rapports" },
    parentSubscription: { icon: "credit-card", label: "Abonnement" },
} satisfies Record<string, TabItem>

export type TabKey = keyof typeof TAB_ITEMS

/** Persona scope for the bottom tab set. */
export type TabType = "student" | "tutor" | "parent"

/**
 * Default ordered tab keys per persona. Tutor production sets may be filtered
 * by ability — Screens/navigators pass an explicit `tabs` override when needed.
 */
export const TABS_BY_TYPE: Record<TabType, TabKey[]> = {
    // Les trois entrées de l'élève : Accueil · Activités · Messages.
    student: ["home", "activities", "tutor"],
    // Les quatre entrées du masteur : Accueil · Mes élèves · Offres de cours · Ressources.
    // « Offres de cours » n'existe qu'avec un droit de mission (l'écran passe son jeu, `tabs`) ;
    // Écoles, Mes classes et Revue d'activités ne sont pas portés (Wajih, 28/10).
    tutor: ["dashboard", "students", "proposals", "tutorResources"],
    // Les quatre entrées du cockpit parent : Accueil · Activités · Rapports · Abonnement.
    parent: ["parentHome", "parentActivities", "parentReports", "parentSubscription"],
}
